import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { AccountSuggestionsSection } from '../configuracion2/sections/AccountSuggestionsSection';
import '../ai/AiSideChat.scss';
import './AccountSuggestionsSidePanel.scss';

/**
 * Modal para revisar sugerencias desde cualquier página (mismo contenido que el módulo completo).
 */
const AccountSuggestionsSidePanel = ({
  open,
  onClose,
  companyId,
  initialDirection = null,
}) => {
  const { t } = useTranslation('header');
  const [overlayVisible, setOverlayVisible] = useState(false);
  const shellRef = useRef(null);

  const handleClose = useCallback(() => {
    setOverlayVisible(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setOverlayVisible(true);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  useEffect(() => {
    if (typeof document === 'undefined' || !open) return undefined;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  if (!open) return null;

  const panel = (
    <div
      className={`ai-sidechat-layer account-suggestions-side-panel-layer ${
        overlayVisible ? 'ai-sidechat-layer--expanded' : ''
      }`}
    >
      <button
        type="button"
        className="ai-sidechat-backdrop"
        aria-label={t('opportunities.panelClose')}
        onClick={handleClose}
        tabIndex={overlayVisible ? 0 : -1}
      />

      <div
        className="ai-sidechat-shell account-suggestions-side-panel-shell ai-sidechat-shell--expanded"
        role="dialog"
        aria-modal="true"
        aria-label={t('opportunities.panelTitle')}
        ref={shellRef}
      >
        <div className="account-suggestions-side-panel">
          <div className="ai-sidechat__header account-suggestions-side-panel__header">
            <div className="ai-sidechat__title">
              <span>{t('opportunities.panelTitle')}</span>
            </div>
            <div className="ai-sidechat__headerActions">
              <button
                type="button"
                onClick={handleClose}
                className="ai-sidechat__iconBtn"
                title={t('opportunities.panelClose')}
                aria-label={t('opportunities.panelClose')}
              >
                <FeatherIcon icon="x" size={18} strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="account-suggestions-side-panel__body">
            {companyId ? (
              <AccountSuggestionsSection
                key={`${companyId}-${String(initialDirection)}`}
                companyId={companyId}
                initialDirection={initialDirection}
              />
            ) : (
              <p className="account-suggestions-side-panel__empty">{t('opportunities.panelNoCompany')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

export default AccountSuggestionsSidePanel;
